// PR-MAY19-CALENDAR-DAYDRILL — drill-down panel for a single day on the
// trips calendar. Reuses the Sheet drawer pattern from TripDetailSheet.
//
// Three sections, top to bottom:
//   1. Anomalies banner — only renders when the day has any flagged
//      vehicles (low / high / missing vs that vehicle's trailing-14d
//      active-day average).
//   2. By-vehicle table — every vehicle that ran trips today plus any
//      "missing" vehicles surfaced by the anomaly query. Click a row to
//      list that vehicle's individual trips for the day (the rendered
//      list is the parent's responsibility — onPickVehicle receives the
//      vehicle id and the parent filters its trip list).
//   3. By-site table — per-site totals with a grand-total footer that
//      MUST sum to total_trips (Playwright asserts this).
//
// The day's individual trip rows are NOT inlined; the parent already
// fetches the calendar's trips, so we let the user click into the
// existing TRP detail drawer from the per-vehicle filter the parent
// applies. Keeps this component small and stateless.

import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, TrendingDown, TrendingUp, X } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { formatTripDate } from '@/lib/format';

export type AnomalyType = 'low' | 'high' | 'missing';

export interface DayVehicleRow {
  vehicle_id: string;
  plate: string | null;
  code: string;
  site_id: string | null;
  site_name: string | null;
  trip_count: number;
  recent_avg: number;
  flag: AnomalyType | null;
}

export interface DaySiteRow {
  site_id: string;
  site_name: string | null;
  trip_count: number;
}

export interface DaySummaryResponse {
  date: string;
  total_trips: number;
  vehicle_count: number;
  site_count: number;
  by_vehicle: DayVehicleRow[];
  by_site: DaySiteRow[];
  anomalies: Array<{ plate: string | null; code: string; trip_count: number; recent_avg: number; type: AnomalyType }>;
}

async function fetchDaySummary(date: string): Promise<DaySummaryResponse> {
  const { data } = await apiClient.get<DaySummaryResponse>('/api/v1/trips/day-summary', { params: { date } });
  return data;
}

interface Props {
  date: string | null;
  onClose: () => void;
  onPickVehicle?: (vehicleId: string, date: string) => void;
}

export function TripDayDrillSheet({ date, onClose, onPickVehicle }: Props) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['trip-day-summary', date],
    queryFn: () => fetchDaySummary(date!),
    enabled: !!date,
  });

  if (!date) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/30 backdrop-blur-sm"
      onClick={onClose}
      data-trip-day-drill
    >
      <div
        className="w-full max-w-2xl bg-card border-s border-border h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 bg-card border-b border-border px-5 py-3 flex items-center justify-between z-10">
          <div>
            <h2 className="font-semibold text-lg">{formatTripDate(date)}</h2>
            {data && (
              <p className="text-xs text-muted-foreground">
                {data.total_trips} trips · {data.vehicle_count} vehicles · {data.site_count} sites
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded hover:bg-muted/40 text-muted-foreground"
            aria-label="Close"
            data-trip-day-drill-close
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="p-5 space-y-5">
          {isLoading && (
            <div className="text-sm text-muted-foreground">Loading day summary…</div>
          )}
          {isError && (
            <div className="rounded border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              Failed to load: {String((error as Error)?.message ?? error)}
            </div>
          )}

          {data && data.anomalies.length > 0 && (
            <section data-trip-day-anomalies>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-2 inline-flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" /> Anomalies ({data.anomalies.length})
              </h3>
              <ul className="rounded border border-amber-500/40 bg-amber-500/5 divide-y divide-amber-500/20">
                {data.anomalies.map((a, i) => (
                  <li key={i} className="px-3 py-2 text-sm flex items-center gap-2">
                    {a.type === 'low'     && <TrendingDown className="h-4 w-4 text-amber-600 shrink-0" />}
                    {a.type === 'missing' && <AlertTriangle className="h-4 w-4 text-rose-600 shrink-0" />}
                    {a.type === 'high'    && <TrendingUp className="h-4 w-4 text-blue-600 shrink-0" />}
                    <span className="font-mono">{a.plate ?? a.code}</span>
                    <span className="text-muted-foreground">
                      — {a.trip_count} trips (usual ~{a.recent_avg}/day) →{' '}
                      <span className="font-medium uppercase text-foreground">{a.type}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {data && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                By vehicle
              </h3>
              <div className="rounded border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/30 border-b border-border">
                    <tr>
                      <th className="text-start px-3 py-2 font-medium text-xs uppercase tracking-wider text-muted-foreground">Vehicle</th>
                      <th className="text-start px-3 py-2 font-medium text-xs uppercase tracking-wider text-muted-foreground">Site</th>
                      <th className="text-end px-3 py-2 font-medium text-xs uppercase tracking-wider text-muted-foreground">Trips</th>
                      <th className="text-start px-3 py-2 font-medium text-xs uppercase tracking-wider text-muted-foreground">Flag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_vehicle.length === 0 ? (
                      <tr><td colSpan={4} className="px-3 py-4 text-center text-muted-foreground">No trips on this day.</td></tr>
                    ) : data.by_vehicle.map((r) => (
                      <tr
                        key={r.vehicle_id}
                        className="border-b border-border last:border-b-0 hover:bg-muted/20 cursor-pointer transition-colors"
                        onClick={() => onPickVehicle?.(r.vehicle_id, date)}
                        data-trip-day-vehicle={r.vehicle_id}
                      >
                        <td className="px-3 py-2 font-mono text-xs">{r.plate ?? r.code}</td>
                        <td className="px-3 py-2 text-muted-foreground">{r.site_name ?? '—'}</td>
                        <td className="px-3 py-2 text-end tabular-nums">
                          {r.trip_count}
                          {r.recent_avg > 0 && (
                            <span className="text-xs text-muted-foreground ms-1">(~{r.recent_avg})</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {r.flag === 'low'     && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/15 text-amber-700 dark:text-amber-400">LOW</span>}
                          {r.flag === 'high'    && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/15 text-blue-700 dark:text-blue-400">HIGH</span>}
                          {r.flag === 'missing' && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-rose-500/15 text-rose-700 dark:text-rose-400">MISSING</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {data && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                By site
              </h3>
              <div className="rounded border border-border overflow-hidden">
                <table className="w-full text-sm" data-trip-day-by-site>
                  <thead className="bg-muted/30 border-b border-border">
                    <tr>
                      <th className="text-start px-3 py-2 font-medium text-xs uppercase tracking-wider text-muted-foreground">Site</th>
                      <th className="text-end px-3 py-2 font-medium text-xs uppercase tracking-wider text-muted-foreground">Trips</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_site.length === 0 ? (
                      <tr><td colSpan={2} className="px-3 py-4 text-center text-muted-foreground">No trips on this day.</td></tr>
                    ) : data.by_site.map((r) => (
                      <tr key={r.site_id} className="border-b border-border last:border-b-0">
                        <td className="px-3 py-2">{r.site_name ?? '—'}</td>
                        <td className="px-3 py-2 text-end tabular-nums">{r.trip_count}</td>
                      </tr>
                    ))}
                  </tbody>
                  {data.by_site.length > 0 && (
                    <tfoot>
                      <tr className="bg-muted/20 border-t border-border font-medium">
                        <td className="px-3 py-2">Total</td>
                        <td className="px-3 py-2 text-end tabular-nums" data-trip-day-total>{data.total_trips}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
